import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiInfo, FiChevronDown, FiAlertTriangle, FiExternalLink } from 'react-icons/fi';
import { RiSwapLine, RiArrowUpDownLine } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { useChain } from '../context/ChainContext';
import { formatBalance } from '../utils/chainUtils';
import Header from './Header';
import TokenIcon from './TokenIcon';
import TokenSelector from './TokenSelector';
import { MAIN_ASSETS } from '../constants/tokens';
import '../styles/SwapPage.css';
import { toast } from 'react-toastify';

// Define third-party swap services
const SWAP_SERVICES = {
  TON: {
    name: 'Dedust.io',
    url: 'https://dedust.io/swap',
    logo: '/assets/partners/dedust.png'
  },
  BNB: {
    name: 'PancakeSwap',
    url: 'https://pancakeswap.finance/swap',
    logo: '/assets/partners/pancakeswap.png' 
  }
};

const SwapPage = () => {
  const navigate = useNavigate();
  const { wallet, balance, tokenBalances, customTokens, chainBalances, tokenPrices, swapTokens } = useWallet();
  const { selectedChain, isTestnet } = useChain();
  
  // State management
  const [fromToken, setFromToken] = useState(MAIN_ASSETS.TON);
  const [toToken, setToToken] = useState(MAIN_ASSETS.BNB);
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [error, setError] = useState('');
  const [isSelectingFrom, setIsSelectingFrom] = useState(false);
  const [isSelectingTo, setIsSelectingTo] = useState(false);
  const [usdValues, setUsdValues] = useState({ from: '0', to: '0' });
  const [swapping, setSwapping] = useState(false);

  // Initialize available tokens
  const availableTokens = useMemo(() => {
    try {
      const networkType = isTestnet ? 'testnet' : 'mainnet';
      const tokens = {
        TON: {
          name: 'TON Chain',
          tokens: {
            TON: {
              ...MAIN_ASSETS.TON,
              balance: chainBalances?.TON || '0',
              price: tokenPrices?.TON?.price || 0
            }
          }
        },
        BNB: {
          name: 'BNB Chain',
          tokens: {
            BNB: {
              ...MAIN_ASSETS.BNB,
              balance: chainBalances?.BNB || '0',
              price: tokenPrices?.BNB?.price || 0
            }
          }
        }
      };

      // Add stablecoins
      ['USDT', 'USDC'].forEach(symbol => {
        if (MAIN_ASSETS[symbol]) {
          const tokenAddress = MAIN_ASSETS[symbol][`${networkType}Address`];
          if (tokenAddress) {
            tokens.TON.tokens[symbol] = {
              ...MAIN_ASSETS[symbol],
              balance: tokenBalances[tokenAddress]?.balance || '0',
              price: 1
            };
          }
        }
      });

      // Add custom tokens
      if (customTokens && typeof customTokens === 'object') {
        Object.entries(customTokens).forEach(([_, token]) => {
          const chain = token?.chain;
          if (chain && tokens[chain]) {
            const tokenAddress = token[`${networkType}Address`] || token.address;
            if (tokenAddress) {
              tokens[chain].tokens[token.symbol] = {
                ...token,
                balance: tokenBalances[tokenAddress]?.balance || '0',
                price: tokenPrices[token.symbol]?.price || 0
              };
            }
          }
        });
      }

      return tokens;
    } catch (error) {
      console.error('Error initializing tokens:', error);
      return {
        TON: { name: 'TON Chain', tokens: {} },
        BNB: { name: 'BNB Chain', tokens: {} }
      };
    }
  }, [chainBalances, tokenPrices, isTestnet, customTokens, tokenBalances]);

  // Get token balance helper
  const getTokenBalance = (token) => {
    if (!token) return '0';
    
    const network = isTestnet ? 'testnet' : 'mainnet';
    
    try {
      switch (token.symbol) {
        case 'TON':
          return chainBalances?.TON || balance || '0';
        case 'BNB':
          return chainBalances?.BNB || '0';
        default: {
          const tokenAddress = token[`${network}Address`] || token.address;
          if (!tokenAddress) return '0';
          return tokenBalances[tokenAddress]?.balance || '0';
        }
      }
    } catch (error) {
      console.error('Error getting token balance:', error);
      return '0';
    }
  };

  // Calculate USD values
  const calculateUsdValues = () => {
    try {
      const fromUsd = fromAmount && tokenPrices[fromToken.symbol]?.price
        ? (parseFloat(fromAmount) * tokenPrices[fromToken.symbol].price).toFixed(2)
        : '0';
      
      const toUsd = toAmount && tokenPrices[toToken.symbol]?.price
        ? (parseFloat(toAmount) * tokenPrices[toToken.symbol].price).toFixed(2)
        : '0';

      setUsdValues({ from: fromUsd, to: toUsd });
    } catch (error) {
      console.error('Error calculating USD values:', error);
      setUsdValues({ from: '0', to: '0' });
    }
  };

  // Handle amount input change
  const handleFromAmountChange = (value) => {
    setFromAmount(value);
    
    if (!value || isNaN(value) || parseFloat(value) <= 0) {
      setToAmount('');
      setError('');
      return;
    }

    try {
      const numValue = parseFloat(value);
      const currentBalance = getTokenBalance(fromToken);
      
      // Validate balance
      if (numValue > parseFloat(currentBalance)) {
        setError(`Insufficient ${fromToken.symbol} balance`);
      } else {
        setError('');
      }

      // Simple estimation based on token prices
      const fromPrice = tokenPrices[fromToken.symbol]?.price || 0;
      const toPrice = tokenPrices[toToken.symbol]?.price || 0;
      
      if (fromPrice > 0 && toPrice > 0) {
        const estimatedAmount = (numValue * fromPrice) / toPrice;
        setToAmount(estimatedAmount.toFixed(toToken.decimals || 6));
      } else {
        setToAmount('');
      }
    } catch (error) {
      console.error('Error handling amount change:', error);
      setError('Failed to calculate swap estimate');
    }
  };

  // Handle token switch
  const handleSwitch = () => {
    setFromToken(toToken);
    setToToken(fromToken);
    setFromAmount('');
    setToAmount('');
    setError('');
  };

  // Get swap service URL with parameters
  const getSwapServiceUrl = () => {
    const service = SWAP_SERVICES[fromToken.chain];
    if (!service) return '#';

    const network = isTestnet ? 'testnet' : 'mainnet';
    let baseUrl = service.url;
    
    // Include custom URL parameters for specific services
    if (service.name === 'Dedust.io') {
      baseUrl += `?fromCurrency=${fromToken.symbol}&toCurrency=${toToken.symbol}`;
      if (fromAmount) baseUrl += `&amount=${fromAmount}`;
    } else if (service.name === 'PancakeSwap') {
      const fromAddress = fromToken[`${network}Address`] || fromToken.address;
      const toAddress = toToken[`${network}Address`] || toToken.address;
      if (fromAddress && toAddress) {
        baseUrl += `?inputCurrency=${fromAddress}&outputCurrency=${toAddress}`;
      }
    }
    
    return baseUrl;
  };

  // Handle external swap
  const handleExternalSwap = () => {
    const url = getSwapServiceUrl();
    window.open(url, '_blank');
  };

  const handleNativeSwap = async () => {
    try {
      setSwapping(true);
      setError('');
      await swapTokens(fromAmount, fromToken, toToken, 50); // 0.5% slippage
      setFromAmount('');
      setToAmount('');
      toast.success('Swap completed!');
    } catch (err) {
      setError(err.message || 'Swap failed');
      toast.error(err.message || 'Swap failed');
    } finally {
      setSwapping(false);
    }
  };

  // Update USD values when amounts change
  useEffect(() => {
    calculateUsdValues();
  }, [fromAmount, toAmount]);

  // Render token input component
  const renderTokenInput = (isFrom) => {
    const token = isFrom ? fromToken : toToken;
    const amount = isFrom ? fromAmount : toAmount;
    const usdValue = isFrom ? usdValues.from : usdValues.to;
    
    if (!token) return null;

    return (
      <div className={`token-input-container ${isFrom ? 'from' : 'to'}`}>
        <div className="input-header">
          <span>{isFrom ? 'From' : 'To (Estimated)'}</span>
          <div className="balance-container">
            <span className="balance">
              Balance: {formatBalance(getTokenBalance(token), token.decimals)} {token.symbol}
            </span>
            {isFrom && parseFloat(getTokenBalance(token)) > 0 && (
              <button 
                className="max-button"
                onClick={() => handleFromAmountChange(getTokenBalance(token))}
              >
                MAX
              </button>
            )}
          </div>
        </div>
        
        <motion.button 
          className="token-select-button"
          onClick={() => isFrom ? setIsSelectingFrom(true) : setIsSelectingTo(true)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          style={{
            width: '100%',
            marginBottom: '10px',
            position: 'relative',
            height: '36px',
            padding: '4px 10px',
            fontSize: '0.9rem',
            justifyContent: 'center'
          }}
        >
          <TokenIcon token={token} size={24} />
          <span>{token.symbol}</span>
          <FiChevronDown />
        </motion.button>
        
        <div className="token-input">
          <div className="amount-container">
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.0"
              value={amount || ''}
              onChange={(e) => isFrom && handleFromAmountChange(e.target.value)}
              pattern="[0-9]*[.]?[0-9]*"
              disabled={!isFrom}
              style={{ width: '100%' }}
            />
            {amount && (
              <div className="usd-value">
                ≈ ${usdValue || '0.00'}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const getEmbeddedSwapWidget = () => {
    // Show PancakeSwap for BNB
    if (fromToken.chain === 'BNB') {
      // PancakeSwap widget only works on mainnet
      if (!isTestnet) {
        return (
          <iframe
            title="PancakeSwap Widget"
            src="https://pancakeswap.finance/swap?chainId=56"
            style={{ width: '100%', height: 600, border: 'none', borderRadius: 16, marginTop: 24 }}
            allow="clipboard-write"
          />
        );
      } else {
        return (
          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <p>PancakeSwap does not support testnet swaps in widget mode.</p>
            <a href="https://testnet.pancakeswap.finance/swap" target="_blank" rel="noopener noreferrer">
              Open PancakeSwap Testnet ↗
            </a>
          </div>
        );
      }
    }
    // Show Dedust for TON
    if (fromToken.chain === 'TON') {
      // Dedust widget only works on mainnet
      if (!isTestnet) {
        return (
          <iframe
            title="Dedust Widget"
            src="https://app.dedust.io/swap"
            style={{ width: '100%', height: 600, border: 'none', borderRadius: 16, marginTop: 24 }}
            allow="clipboard-write"
          />
        );
      } else {
        return (
          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <p>Dedust does not support testnet swaps in widget mode.</p>
            <a href="https://testnet.dedust.io/swap" target="_blank" rel="noopener noreferrer">
              Open Dedust Testnet ↗
            </a>
          </div>
        );
      }
    }
    // Fallback
    return null;
  };

  return (
    <div className="swap-page">
      <Header title="Swap" className="sticky-header" />
      
      <div className="content">
        {/* Hide the old swap form if widget is available */}
        {getEmbeddedSwapWidget() ? null : (
          <motion.div 
            className="swap-card"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="swap-header">
              <h2>Swap Tokens</h2>
            </div>

            <div className="swap-form">
              {/* From Token Input */}
              {renderTokenInput(true)}

              {/* Switch Button */}
              <motion.button
                className="switch-button"
                onClick={handleSwitch}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <RiArrowUpDownLine />
              </motion.button>

              {/* To Token Input */}
              {renderTokenInput(false)}

              {/* Swap Details */}
              <div className="swap-details">
                <div className="detail-row">
                  <span>Service Provider</span>
                  <span className="swap-provider">{SWAP_SERVICES[fromToken.chain]?.name || 'Not available'}</span>
                </div>
                
                <div className="detail-row">
                  <span>Network</span>
                  <span>{isTestnet ? 'Testnet' : 'Mainnet'}</span>
                </div>
              </div>

              {/* Error Messages */}
              <AnimatePresence>
                {error && (
                  <motion.div 
                    className="error-message"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                  >
                    <FiAlertTriangle />
                    <span>{error}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="external-service-notice">
                <FiInfo size={16} />
                <span>Swaps will be processed by {SWAP_SERVICES[fromToken.chain]?.name || 'an external service'}</span>
              </div>

              {/* External Swap Button */}
              {(selectedChain === 'TON' || selectedChain === 'BNB') ? (
                <motion.button
                  className={`swap-button active${swapping ? ' loading' : ''}`}
                  onClick={handleNativeSwap}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={!fromAmount || parseFloat(fromAmount) <= 0 || error || swapping}
                >
                  <RiSwapLine />
                  <span>{swapping ? 'Swapping...' : 'Swap'}</span>
                </motion.button>
              ) : (
                <motion.button
                  className="swap-button active"
                  onClick={handleExternalSwap}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={!fromAmount || parseFloat(fromAmount) <= 0 || error}
                >
                  <RiSwapLine />
                  <span>Swap on {SWAP_SERVICES[fromToken.chain]?.name}</span>
                  <FiExternalLink size={16} />
                </motion.button>
              )}
            </div>
          </motion.div>
        )}
        {/* Show the widget below or instead of the form */}
        {getEmbeddedSwapWidget()}
      </div>

      {/* Token Selectors */}
      {isSelectingFrom && (
        <TokenSelector
          isOpen={isSelectingFrom}
          onClose={() => setIsSelectingFrom(false)}
          onSelect={(token) => {
            setFromToken(token);
            setIsSelectingFrom(false);
            setFromAmount('');
            setToAmount('');
            setError('');
          }}
          selectedToken={fromToken}
          tokens={availableTokens}
        />
      )}

      {isSelectingTo && (
        <TokenSelector
          isOpen={isSelectingTo}
          onClose={() => setIsSelectingTo(false)}
          onSelect={(token) => {
            setToToken(token);
            setIsSelectingTo(false);
            setFromAmount(fromAmount); // Recalculate estimates
          }}
          selectedToken={toToken}
          tokens={availableTokens}
        />
      )}
    </div>
  );
};

export default SwapPage;
